import { BOOKING_URL, trackBooking } from "@/lib/booking";
import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { CalendarDays, CalendarClock, Check, Printer, ArrowRight, Sparkles, Sun, Clock, MapPin } from "lucide-react";
import { festivalBySlug } from "@/data/festivals";
import { STOTRAS } from "@/data/stotras";
import { useSeo } from "@/lib/seo";
import { StotraHeader } from "../stotras/StotraHub";
import { WhatsAppShare } from "@/components/ShareButtons";
import { KA_CITIES } from "@/lib/cities";

const API = process.env.REACT_APP_BACKEND_URL;

export default function FestivalPage() {
  const { slug } = useParams();
  const f = festivalBySlug(slug);
  const [panch, setPanch] = useState(null);
  const [cityId, setCityId] = useState("bengaluru");
  const city = KA_CITIES.find((c) => c.id === cityId) || KA_CITIES[0];
  useEffect(() => { window.scrollTo(0, 0); }, [slug]);
  useEffect(() => {
    if (!f) return;
    setPanch(null);
    fetch(`${API}/api/panchanga?date=${f.date}&lat=${city.lat}&lng=${city.lng}`)
      .then((r) => { if (!r.ok) throw new Error("bad"); return r.json(); })
      .then(setPanch).catch(() => setPanch(null));
  }, [f, city.lat, city.lng]);

  useSeo({
    title: f ? `${f.nameEn} 2026 — ${f.nameKn} | Date, Pooja Vidhi & Stotras | PurohithConnect` : "Festival not found",
    description: f ? `${f.nameEn} 2026 date: ${f.dateText}. ${f.significance.en}` : "",
    canonicalPath: `/festivals/${slug}`,
    jsonLd: f && [
      {
        "@context": "https://schema.org", "@type": "Event",
        name: `${f.nameEn} 2026`, startDate: f.date,
        eventAttendanceMode: "https://schema.org/OfflineEventAttendanceMode",
        location: { "@type": "Place", name: "Bengaluru, Karnataka, India" },
        image: `https://purohithconnect.com${f.img}`,
        description: f.significance.en,
        organizer: { "@type": "Organization", name: "PurohithConnect", url: "https://purohithconnect.com" },
      },
      {
        "@context": "https://schema.org", "@type": "FAQPage",
        mainEntity: f.faqs.map((x) => ({ "@type": "Question", name: x.q, acceptedAnswer: { "@type": "Answer", text: x.a } })),
      },
      {
        "@context": "https://schema.org", "@type": "HowTo",
        name: `How to prepare for ${f.nameEn} 2026`,
        description: `Preparation checklist for ${f.nameEn} — samagri to buy, home preparation and what to arrange in advance.`,
        step: (f.prepare || []).map((p, i) => ({ "@type": "HowToStep", position: i + 1, name: p })),
      },
      {
        "@context": "https://schema.org", "@type": "BreadcrumbList",
        itemListElement: [
          { "@type": "ListItem", position: 1, name: "Festival Calendar", item: "https://purohithconnect.com/festivals" },
          { "@type": "ListItem", position: 2, name: f.nameEn, item: `https://purohithconnect.com/festivals/${slug}` },
        ],
      },
    ],
  });

  if (!f) {
    return (
      <div className="min-h-screen bg-[#FBF6EC]">
        <StotraHeader />
        <div className="py-24 text-center font-mukta text-[#6B5044]">
          Festival not found. <Link to="/festivals" className="text-[#B23A2B] underline">Back to calendar</Link>
        </div>
      </div>
    );
  }

  const related = f.relatedStotras.map((s) => STOTRAS.find((x) => x.slug === s)).filter(Boolean);

  const today = new Date(); today.setHours(0, 0, 0, 0);
  const daysToGo = Math.round((new Date(f.date + "T00:00:00") - today) / 86400000);

  const samagriText = `${f.nameEn} (${f.nameKn}) 2026 — ${f.dateText}\nSamagri & preparation checklist:\n${(f.prepare || []).map((p, i) => `${i + 1}. ${p}`).join("\n")}\n\nFull pooja vidhi & stotras: https://purohithconnect.com/festivals/${f.slug}`;

  const printSamagri = () => {
    const items = (f.prepare || []).map((p, i) => `<li><b>${i + 1}.</b> ${p}</li>`).join("");
    const w = window.open("", "_blank", "width=760,height=900");
    if (!w) return;
    w.document.write(`<!doctype html><html><head><meta charset="utf-8"><title>${f.nameEn} — Samagri checklist</title>
<style>
  body{font-family:Georgia,'Times New Roman',serif;color:#3B221B;margin:0;padding:40px;background:#FBF6EC}
  .card{max-width:640px;margin:0 auto;border:2px solid #A8722A;border-radius:16px;padding:32px;background:#FFFDF8}
  h1{font-size:26px;margin:0}
  .kn{color:#A8722A;font-size:18px;margin:4px 0 0}
  .date{color:#B23A2B;font-weight:bold;margin:8px 0 20px}
  h2{font-size:16px;border-bottom:1px solid #A8722A;padding-bottom:6px;margin:20px 0 12px}
  ul{padding:0;list-style:none;margin:0}
  li{font-family:Arial,Helvetica,sans-serif;font-size:15px;line-height:1.5;padding:9px 0;border-bottom:1px dashed #A8722A55}
  li b{color:#B23A2B;margin-right:6px}
  footer{margin-top:24px;font-family:Arial,sans-serif;font-size:12px;color:#6B5044;text-align:center}
</style></head><body><div class="card">
<h1>${f.nameEn}</h1><p class="kn">${f.nameKn}</p><p class="date">${f.dateText}</p>
<h2>Samagri &amp; preparation checklist</h2><ul>${items}</ul>
<footer>PurohithConnect &middot; purohithconnect.com &middot; Book verified purohiths in Bengaluru</footer>
</div><script>window.onload=function(){window.print();}<\/script></body></html>`);
    w.document.close();
  };

  return (
    <div className="min-h-screen bg-[#FBF6EC] text-[#3B221B]">
      <StotraHeader />
      <main className="mx-auto max-w-4xl px-4 pb-20 md:px-8">
        <nav className="pt-5 font-mukta text-sm" aria-label="Breadcrumb">
          <Link to="/festivals" className="text-[#A8722A] hover:underline" data-testid="festival-breadcrumb">← Festival Calendar 2026</Link>
        </nav>
        <article className="mt-4">
          <div className="overflow-hidden rounded-[14px] border border-[#A8722A]/30">
            <img src={f.img} alt={f.nameEn} className="h-64 w-full object-cover object-top md:h-80" />
          </div>
          <h1 className="mt-6 text-center font-eczar text-3xl font-bold md:text-4xl" data-testid="festival-title">{f.nameEn}</h1>
          <p className="mt-1 text-center font-baloo text-xl text-[#A8722A]">{f.nameKn}</p>
          <p className="mt-3 flex items-center justify-center gap-2 font-mukta text-sm font-semibold text-[#B23A2B]" data-testid="festival-date">
            <CalendarDays size={16} /> {f.dateText}
          </p>
          {daysToGo > 0 && (
            <div className="mx-auto mt-3 flex w-fit items-center gap-2 rounded-full border border-[#B23A2B]/30 bg-[#B23A2B]/10 px-4 py-1.5 font-mukta text-sm font-semibold text-[#B23A2B]" data-testid="festival-countdown">
              <CalendarClock size={15} /> {daysToGo} {daysToGo === 1 ? "day" : "days"} to go
            </div>
          )}
          {daysToGo === 0 && (
            <div className="mx-auto mt-3 flex w-fit items-center gap-2 rounded-full border border-[#3E6B4F]/30 bg-[#3E6B4F]/10 px-4 py-1.5 font-mukta text-sm font-semibold text-[#3E6B4F]" data-testid="festival-countdown">
              <CalendarClock size={15} /> Today — {f.nameEn}!
            </div>
          )}
          {daysToGo < 0 && (
            <div className="mx-auto mt-3 w-fit rounded-full border border-[#A8722A]/30 bg-[#F3EADA] px-4 py-1.5 font-mukta text-sm text-[#6B5044]" data-testid="festival-countdown">
              This year's {f.nameEn} was on {f.dateText}
            </div>
          )}
          <div className="mt-4 flex justify-center">
            <WhatsAppShare text={`${f.nameEn} (${f.nameKn}) 2026 — ${f.dateText}. Pooja vidhi & stotras: https://purohithconnect.com/festivals/${f.slug}`} />
          </div>

          <div className="mt-6 rounded-[14px] border border-[#A8722A]/25 bg-[#F3EADA]/70 p-5">
            <p className="font-baloo text-[15px]">{f.significance.kn}</p>
            <p className="mt-2 font-mukta text-sm text-[#6B5044]">{f.significance.en}</p>
          </div>

          {panch && (
            <div className="mt-4 rounded-[14px] border border-[#A8722A]/25 bg-[#FFFDF8] p-5" data-testid="festival-panchanga">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <p className="inline-flex items-center gap-2 font-eczar text-sm font-semibold text-[#3B221B]">
                  <Sparkles size={16} className="text-[#A8722A]" /> Panchanga on this day · ಈ ದಿನದ ಪಂಚಾಂಗ
                  <span className="rounded-full bg-[#A8722A]/12 px-2 py-0.5 font-mukta text-[10px] font-medium text-[#A8722A]">live-computed</span>
                </p>
                <div className="inline-flex items-center gap-1.5 rounded-full border border-[#A8722A]/30 bg-[#FBF6EC] pl-2.5 pr-1 py-0.5">
                  <MapPin size={13} className="text-[#A8722A]" />
                  <select value={cityId} onChange={(e) => setCityId(e.target.value)} data-testid="festival-city-select"
                    className="bg-transparent py-1 pr-1 font-mukta text-xs font-semibold text-[#3B221B] outline-none">
                    {KA_CITIES.map((c) => <option key={c.id} value={c.id}>{c.en} · {c.kn}</option>)}
                  </select>
                </div>
              </div>
              <div className="mt-3 grid grid-cols-2 gap-3 sm:grid-cols-4">
                <div data-testid="festival-panch-tithi"><p className="font-baloo text-xs text-[#B23A2B]">ತಿಥಿ · Tithi</p><p className="font-eczar text-sm font-semibold">{panch.tithi.paksha} {panch.tithi.name}</p></div>
                <div data-testid="festival-panch-nakshatra"><p className="font-baloo text-xs text-[#B23A2B]">ನಕ್ಷತ್ರ · Nakshatra</p><p className="font-eczar text-sm font-semibold">{panch.nakshatra.name}</p></div>
                <div><p className="font-baloo text-xs text-[#B23A2B]">ವಾರ · Vara</p><p className="font-eczar text-sm font-semibold">{panch.vara}</p></div>
                <div><p className="inline-flex items-center gap-1 font-baloo text-xs text-[#B23A2B]"><Sun size={11} /> Sunrise</p><p className="font-eczar text-sm font-semibold">{panch.sunrise}</p></div>
              </div>
              <p className="mt-3 font-mukta text-xs text-[#6B5044]">
                Abhijit Muhurta {panch.abhijit_muhurta.start}–{panch.abhijit_muhurta.end} · avoid Rahu Kaala {panch.inauspicious.rahu_kaala.start}–{panch.inauspicious.rahu_kaala.end}.
                {" "}<Link to="/panchanga" className="font-semibold text-[#B23A2B] hover:underline" data-testid="festival-panchanga-link">Full panchanga & muhurta →</Link>
              </p>
            </div>
          )}

          {panch && (() => {
            const windows = [
              { label: "Abhijit Muhurta", kn: "ಅಭಿಜಿತ್ ಮುಹೂರ್ತ", start: panch.abhijit_muhurta.start, end: panch.abhijit_muhurta.end, best: true },
              ...panch.choghadiya_day
                .filter((c) => c.good && !c.blocked)
                .map((c) => ({ label: `${c.name} Choghadiya`, kn: `${c.name_kn} ಚೌಘಡಿಯ`, start: c.start, end: c.end })),
            ];
            return (
              <div className="mt-4 rounded-[14px] border border-[#2f6b4533] bg-[#EAF3EC]/60 p-5" data-testid="festival-muhurta-windows">
                <p className="inline-flex items-center gap-2 font-eczar text-sm font-semibold text-[#2f6b45]">
                  <Clock size={16} /> Best times to book &amp; perform · ಶುಭ ಸಮಯ
                </p>
                <p className="mt-1 font-mukta text-xs text-[#6B5044]">Auspicious windows on this day, computed by our muhurta engine (avoiding Rahu Kaala, Yamaganda &amp; Gulika).</p>
                <div className="mt-3 flex flex-wrap gap-2">
                  {windows.map((w, i) => (
                    <div key={i} className={`rounded-[10px] border px-3 py-2 ${w.best ? "border-[#A8722A]/50 bg-[#FBEEDA]" : "border-[#2f6b4533] bg-[#FFFDF8]"}`} data-testid={`festival-window-${i}`}>
                      <p className={`font-baloo text-xs ${w.best ? "text-[#8f5f21]" : "text-[#2f6b45]"}`}>{w.kn}</p>
                      <p className="font-eczar text-sm font-semibold text-[#3B221B]">{w.start} – {w.end}</p>
                      <p className="font-mukta text-[11px] text-[#6B5044]">{w.label}{w.best ? " · most auspicious" : ""}</p>
                    </div>
                  ))}
                </div>
                <a href={BOOKING_URL} target="_blank" rel="noopener noreferrer" onClick={() => trackBooking({ location: "festival-muhurta" })}
                  className="mt-4 inline-flex items-center gap-2 rounded-full bg-[#B23A2B] px-5 py-2.5 font-mukta text-sm font-semibold text-[#FFFDF8] hover:bg-[#9a2f22]" data-testid="festival-muhurta-book">
                  Book a purohith for {f.nameEn} <ArrowRight size={15} />
                </a>
              </div>
            );
          })()}

          {f.muhurta && (
            <div className="mt-4 flex items-start gap-3 rounded-[14px] border border-[#B23A2B]/25 bg-[#B23A2B]/[0.05] p-4" data-testid="festival-muhurta">
              <CalendarClock size={18} className="mt-0.5 shrink-0 text-[#B23A2B]" />
              <div>
                <p className="font-eczar text-sm font-semibold text-[#3B221B]">Auspicious muhurta · ಶುಭ ಮುಹೂರ್ತ</p>
                <p className="mt-0.5 font-mukta text-sm text-[#6B5044]">{f.muhurta}</p>
              </div>
            </div>
          )}

          <section className="mt-8" data-testid="festival-prepare-section">
            <h2 className="font-eczar text-xl font-semibold">How to prepare · ಪೂರ್ವ ಸಿದ್ಧತೆ</h2>
            <p className="mt-1 font-mukta text-sm text-[#6B5044]">Get everything ready before the day — samagri to buy, home preparation and what to arrange in advance.</p>
            <ul className="mt-3 space-y-2">
              {(f.prepare || []).map((p, i) => (
                <li key={i} className="flex items-start gap-3 rounded-[12px] border border-[#A8722A]/20 bg-[#FFFDF8] px-3.5 py-2.5 font-mukta text-[15px] text-[#6B5044]" data-testid={`festival-prepare-${i}`}>
                  <span className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full border border-[#B23A2B]/40 text-[11px] font-bold text-[#B23A2B]">{i + 1}</span>
                  {p}
                </li>
              ))}
            </ul>
            <div className="mt-4 flex flex-wrap gap-3">
              <button onClick={printSamagri} data-testid="samagri-print-btn"
                className="inline-flex items-center gap-1.5 rounded-full border border-[#A8722A]/40 px-4 py-1.5 font-mukta text-sm font-semibold text-[#A8722A] transition-colors hover:bg-[#A8722A] hover:text-[#FFFDF8]">
                <Printer size={15} /> Save / print samagri card
              </button>
              <WhatsAppShare text={samagriText} testId="samagri-whatsapp-btn" label="Send checklist on WhatsApp" />
            </div>
          </section>

          <section className="mt-8">
            <h2 className="font-eczar text-xl font-semibold">Pooja vidhi · ಪೂಜಾ ವಿಧಿ</h2>
            <ul className="mt-3 space-y-2">
              {f.vidhi.map((v, i) => (
                <li key={i} className="flex items-start gap-2 font-mukta text-[15px] text-[#6B5044]">
                  <Check size={16} className="mt-0.5 shrink-0 text-[#3E6B4F]" /> {v}
                </li>
              ))}
            </ul>
          </section>

          <section className="mt-8">
            <h2 className="font-eczar text-xl font-semibold">Stotras for this festival · ಸ್ತೋತ್ರಗಳು</h2>
            <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-3">
              {related.map((s) => (
                <Link key={s.slug} to={`/stotras/${s.slug}`} data-testid={`festival-stotra-${s.slug}`}
                  className="rounded-[14px] border border-[#A8722A]/25 bg-[#FFFDF8] p-4 shadow-sm hover:shadow-md">
                  <p className="font-baloo text-sm text-[#A8722A]">{s.titleKn}</p>
                  <p className="font-eczar text-sm font-semibold">{s.titleEn}</p>
                </Link>
              ))}
            </div>
          </section>

          {f.parayana && (
            <section className="mt-8" data-testid="festival-parayana-cta">
              <div className="flex flex-col items-start gap-4 rounded-[16px] border border-[#3E6B4F]/30 bg-[#3E6B4F]/[0.06] p-5 sm:flex-row sm:items-center sm:justify-between">
                <div>
                  <p className="font-baloo text-sm text-[#3E6B4F]">ಗುಂಪು ಪಾರಾಯಣ</p>
                  <p className="mt-0.5 font-eczar text-[15px] font-semibold text-[#3B221B]">Mark {f.nameEn} with a group parayana</p>
                  <p className="mt-1 font-mukta text-sm text-[#6B5044]">A group parayana of <span className="font-semibold text-[#3B221B]">{f.parayana}</span> is a beautiful way to observe {f.nameEn} at home or a temple.</p>
                </div>
                <Link to="/parayana" data-testid="festival-parayana-link"
                  className="inline-flex shrink-0 items-center gap-1.5 rounded-full bg-[#3E6B4F] px-5 py-2.5 font-mukta text-sm font-semibold text-white transition-colors hover:bg-[#2c5039]">
                  Book a parayana <ArrowRight size={15} />
                </Link>
              </div>
            </section>
          )}

          <section className="mt-10">
            <h2 className="font-eczar text-xl font-semibold">Frequently asked · ಪ್ರಶ್ನೋತ್ತರ</h2>
            <div className="mt-3 space-y-3">
              {f.faqs.map((x, i) => (
                <div key={i} className="rounded-[14px] border border-[#A8722A]/25 bg-[#FFFDF8] p-4" data-testid={`festival-faq-${i}`}>
                  <p className="font-eczar text-[15px] font-semibold">{x.q}</p>
                  <p className="mt-1 font-mukta text-sm text-[#6B5044]">{x.a}</p>
                </div>
              ))}
            </div>
          </section>

          <div className="mt-10 rounded-[14px] bg-[#3B221B] p-6 text-center">
            <p className="font-eczar text-lg font-semibold text-[#FBF6EC]">Need a purohith for {f.nameEn}?</p>
            <p className="mt-1 font-mukta text-sm text-[#FBF6EC]/70">KYC-verified purohiths in Bengaluru — book in minutes.</p>
            <a href={BOOKING_URL} target="_blank" rel="noopener noreferrer" onClick={() => trackBooking({ location: "festival-page" })} className="mt-4 inline-block rounded-full bg-[#B23A2B] px-6 py-2.5 font-mukta font-semibold text-[#FFFDF8] hover:bg-[#9a2f22]" data-testid="festival-cta">
              Book a Purohith ↗
            </a>
          </div>
        </article>
      </main>
      <footer className="border-t border-[#A8722A]/20 bg-[#F3EADA] py-5 text-center font-mukta text-sm text-[#6B5044]">
        © 2026 PurohithConnect · Built in Bengaluru, Karnataka — for India
      </footer>
    </div>
  );
}
